import {Component, Inject} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, ValidationErrors, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { v4 } from 'uuid';
import { LinqsetNode, NODE_SUPERTYPE, NODE_TYPE } from '../../common/linqset-model';
import { ASSET_CLASSIFICATION, ASSET_CLASSIFICATION_LABELS, INFORMATION_ASSET_DESCRIPTION_MAX, INFORMATION_ASSET_NAME_MAX, INFORMATION_ASSET_NAME_MIN, InformationAsset } from '../../common/organisation';
import { OrganisationService } from '../../organisation/organisation.service';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = form && form.submitted;
      return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

// a role or system an asset's owner or system of record can be
export interface AssetNodeChoice {
    key: string
    name: string
}

export interface InformationAssetDialogData {
    asset: InformationAsset
    roles: AssetNodeChoice[]        // the organisation's roles, for its owner
    systems: AssetNodeChoice[]      // the organisation's systems, for its system of record
}

const byName = (a: AssetNodeChoice, b: AssetNodeChoice) => a.name.localeCompare(b.name)

// the roles and systems of the organisation model an asset's owner and system of record are picked from
export const assetNodeChoices = (organisationNodes: LinqsetNode[] = []) => ({
    roles: organisationNodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE && n.type !== NODE_TYPE.EXTERNAL_ORGANISATION)
        .map(n => ({ key: n._key, name: n.name })).sort(byName),
    systems: organisationNodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM).map(n => ({ key: n._key, name: n.name })).sort(byName),
})

// opens the dialog for a new asset, or to change one, and saves it. Returns the asset saved, or undefined when it was
// cancelled or couldn't be saved
export const editInformationAsset = async (dialog: MatDialog, organisationService: OrganisationService, asset: InformationAsset | undefined,
    organisationNodes: LinqsetNode[] = []): Promise<InformationAsset | undefined> => {
    const data: InformationAssetDialogData = { asset: asset || {} as InformationAsset, ...assetNodeChoices(organisationNodes) }
    const result: InformationAsset | undefined = await firstValueFrom(dialog.open(InformationAssetDialogComponent, { data, hasBackdrop: true, width: '600px' }).afterClosed())
    if (!result) return undefined
    if (!asset) return organisationService.createInformationAsset({ ...result, _key: v4() })
    return organisationService.updateInformationAsset(result)
}

// creates or edits an information asset: the information itself, such as a customer order, which information nodes
// are states of, with its register details
@Component({
    selector: 'app-information-asset-dialog',
    templateUrl: 'information-asset-dialog.html',
    styleUrls: ['information-asset-dialog.css'],
    standalone: false
})
export class InformationAssetDialogComponent {
    dialogForm: FormGroup
    matcher = new MyErrorStateMatcher();
    isNew:boolean
    INFORMATION_ASSET_NAME_MIN = INFORMATION_ASSET_NAME_MIN
    INFORMATION_ASSET_NAME_MAX = INFORMATION_ASSET_NAME_MAX
    INFORMATION_ASSET_DESCRIPTION_MAX = INFORMATION_ASSET_DESCRIPTION_MAX
    classifications = Object.values(ASSET_CLASSIFICATION).map(c => ({ value: c, label: ASSET_CLASSIFICATION_LABELS[c] }))
    roles: AssetNodeChoice[]
    systems: AssetNodeChoice[]

    constructor(public dialogRef: MatDialogRef<InformationAssetDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InformationAssetDialogData,
        private formBuilder: FormBuilder, private organisationService:OrganisationService) {

        const asset = data.asset
        this.isNew = !asset?._key
        // an owner or system of record no longer in the organisation stays a choice, so it isn't cleared unseen
        const keep = (choices: AssetNodeChoice[], key?: string | null) => key && !choices.some(c => c.key === key) ? [...choices, { key, name: 'No longer in the organisation' }] : choices
        this.roles = keep(data.roles || [], asset.ownerKey)
        this.systems = keep(data.systems || [], asset.systemOfRecordKey)
        this.dialogForm  =  this.formBuilder.group({
            name: [asset.name, [Validators.required, Validators.minLength(INFORMATION_ASSET_NAME_MIN), Validators.maxLength(INFORMATION_ASSET_NAME_MAX), this.uniqueName]],
            description: [asset.description, Validators.maxLength(INFORMATION_ASSET_DESCRIPTION_MAX)],
            ownerKey: [asset.ownerKey || null],
            classification: [asset.classification || null],
            systemOfRecordKey: [asset.systemOfRecordKey || null],
        })
    }

    // two assets with the same name can't be told apart in the lists
    private uniqueName = (control:AbstractControl):ValidationErrors | null => {
        const name = (control.value || '').trim().toLowerCase()
        const clash = name && this.organisationService.organisation?.informationAssets?.some(a => a._key !== this.data.asset?._key && (a.name || '').trim().toLowerCase() === name)
        return clash ? { duplicate: true } : null
    }

    getNameError() {
        return `Name must be between ${INFORMATION_ASSET_NAME_MIN} and ${INFORMATION_ASSET_NAME_MAX} characters`
    }

    getDescriptionError() {
        return `Description must be ${INFORMATION_ASSET_DESCRIPTION_MAX} characters or fewer`
    }

    // closes with an updated copy; the caller saves it and only then changes the asset
    save() {
        this.dialogForm.markAllAsTouched()
        if (this.dialogForm.invalid) return

        const v = this.dialogForm.value
        this.dialogRef.close({
            ...this.data.asset,
            name: v.name.trim(),
            description: (v.description || '').trim(),
            ownerKey: v.ownerKey || null,
            classification: v.classification || null,
            systemOfRecordKey: v.systemOfRecordKey || null,
        } as InformationAsset);
    }
}
